import { useState, useEffect } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import * as RadixTabs from "@radix-ui/react-tabs";

import {
  getGeminiApiKey,
  setGeminiApiKey,
  clearGeminiApiKey,
  testGeminiApiKey,
} from "../data/gemini";

import "./SettingsDialog.scss";

interface SettingsDialogProps {
  onClose: () => void;
}

export const SettingsDialog = ({ onClose }: SettingsDialogProps) => {
  const [apiKey, setApiKeyState] = useState("");
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState("ai-settings");
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    const existingKey = getGeminiApiKey();
    if (existingKey) {
      setApiKeyState(existingKey);
    }
  }, []);

  const handleSave = () => {
    if (apiKey.trim()) {
      setGeminiApiKey(apiKey.trim());
      setSaved(true);
      setTestResult(null);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
    }
  };

  const handleTest = async () => {
    if (!apiKey.trim()) {
      setTestResult({
        success: false,
        message: "Please enter an API key first",
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const result = await testGeminiApiKey(apiKey.trim());
      setTestResult({
        success: result.success,
        message: result.message,
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: "Connection test failed",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClear = () => {
    if (confirm("Are you sure you want to clear your API key?")) {
      clearGeminiApiKey();
      setApiKeyState("");
      setSaved(false);
      setTestResult(null);
    }
  };

  return (
    <Dialog
      onCloseRequest={onClose}
      title="Settings"
      size="wide"
      className="settings-dialog"
    >
      <RadixTabs.Root value={activeTab} onValueChange={setActiveTab}>
        <RadixTabs.List className="settings-dialog__tabs">
          <RadixTabs.Trigger
            value="ai-settings"
            className="settings-dialog__tab-trigger"
          >
            AI Settings
          </RadixTabs.Trigger>
          <RadixTabs.Trigger
            value="general"
            className="settings-dialog__tab-trigger"
          >
            General
          </RadixTabs.Trigger>
        </RadixTabs.List>

        <RadixTabs.Content
          value="ai-settings"
          className="settings-dialog__tab-content"
        >
          <div className="settings-dialog__section">
            <h3 className="settings-dialog__section-title">Gemini API Key</h3>
            <p className="settings-dialog__section-description">
              Configure your Gemini API key to enable AI-powered features like
              text-to-diagram and diagram-to-code generation.
            </p>

            <div className="settings-dialog__field-group">
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 500,
                    marginBottom: "8px",
                    color: "var(--color-gray-80)",
                  }}
                >
                  API Key
                </label>
                <input
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => {
                    setApiKeyState(e.target.value);
                    setSaved(false);
                    setTestResult(null);
                  }}
                  placeholder="Enter your Gemini API key"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "14px",
                    border: "1px solid var(--color-gray-20)",
                    borderRadius: "4px",
                    backgroundColor: "var(--color-gray-05)",
                    color: "var(--color-gray-80)",
                  }}
                />
              </div>

              <div className="settings-dialog__field-actions">
                <button
                  className="settings-dialog__btn settings-dialog__btn--secondary"
                  onClick={() => setShowApiKey(!showApiKey)}
                  type="button"
                >
                  {showApiKey ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="settings-dialog__actions">
              <button
                className="settings-dialog__btn settings-dialog__btn--primary"
                onClick={handleSave}
                disabled={!apiKey.trim()}
                type="button"
              >
                {saved ? "✓ Saved" : "Save"}
              </button>

              <button
                className="settings-dialog__btn settings-dialog__btn--secondary"
                onClick={handleTest}
                disabled={!apiKey.trim() || testing}
                type="button"
              >
                {testing ? "Testing..." : "Test Connection"}
              </button>

              <button
                className="settings-dialog__btn settings-dialog__btn--danger"
                onClick={handleClear}
                disabled={!apiKey.trim()}
                type="button"
              >
                Clear
              </button>
            </div>

            {testResult && (
              <div
                className={`settings-dialog__status ${
                  testResult.success
                    ? "settings-dialog__status--success"
                    : "settings-dialog__status--error"
                }`}
              >
                {testResult.success ? "✓ " : "✗ "}
                {testResult.message}
              </div>
            )}

            <div className="settings-dialog__help">
              <p>
                <strong>How to get an API key:</strong>
              </p>
              <ol>
                <li>
                  Visit{" "}
                  <a
                    href="https://aistudio.google.com/app/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google AI Studio
                  </a>
                </li>
                <li>Sign in with your Google account</li>
                <li>Click "Get API Key" or "Create API Key"</li>
                <li>Copy the key and paste it above</li>
              </ol>
              <p className="settings-dialog__warning">
                ⚠️ Your API key is stored locally in your browser and never sent
                to our servers. Keep it secure and don't share it.
              </p>
            </div>
          </div>
        </RadixTabs.Content>

        <RadixTabs.Content
          value="general"
          className="settings-dialog__tab-content"
        >
          <div className="settings-dialog__section">
            <h3 className="settings-dialog__section-title">General Settings</h3>
            <p className="settings-dialog__section-description">
              Additional settings will be available here in future updates.
            </p>
          </div>
        </RadixTabs.Content>
      </RadixTabs.Root>
    </Dialog>
  );
};
